import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Globe, ChevronDown, User, LogOut, LayoutDashboard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ConexusLogo from '@/components/logos/ConexusLogo';
import FlagIcon from '@/components/FlagIcon';
import { useAuth } from '@/contexts/AuthContext';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, profile, loading, signOut } = useAuth();

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/programs', label: t('nav.programs') },
    { path: '/stories', label: t('nav.stories') },
    { path: '/resources', label: t('nav.resources') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const languages: Array<{ code: 'en' | 'pt' | 'es' | 'fr' | 'it' | 'zh'; label: string; country: 'US' | 'BR' | 'ES' | 'FR' | 'IT' | 'CN' }> = [
    { code: 'en', label: 'English', country: 'US' },
    { code: 'pt', label: 'Português', country: 'BR' },
    { code: 'es', label: 'Español', country: 'ES' },
    { code: 'fr', label: 'Français', country: 'FR' },
    { code: 'it', label: 'Italiano', country: 'IT' },
    { code: 'zh', label: '中文', country: 'CN' },
  ];

  // currentLanguage no longer needed — all options are visible in the segmented switcher

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <nav className="container mx-auto px-4 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <ConexusLogo className="h-10 w-auto" variant="full" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-5 xl:gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-primary ${
                location.pathname === link.path
                  ? 'text-primary'
                  : 'text-foreground/80'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right Side Actions */}
        <div className="hidden lg:flex items-center gap-2 xl:gap-3">
          {/* Language Selector — compact dropdown showing current flag + code */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label={t("common.selectLanguage")}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full border border-border bg-muted/40 hover:bg-muted transition-colors text-xs font-semibold text-foreground/80 hover:text-foreground"
              >
                <FlagIcon
                  country={(languages.find(l => l.code === i18n.language)?.country) || 'US'}
                  className="h-3 w-[18px] rounded-sm"
                />
                <span className="uppercase tracking-wide">{i18n.language?.slice(0, 2) || 'en'}</span>
                <ChevronDown className="h-3 w-3 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44 bg-background">
              {languages.map((lang) => {
                const active = i18n.language === lang.code;
                return (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex items-center gap-2.5 cursor-pointer ${active ? 'bg-muted font-semibold' : ''}`}
                  >
                    <FlagIcon country={lang.country} className="h-3 w-[18px] rounded-sm" />
                    <span className="flex-1 text-sm">{lang.label}</span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{lang.code}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>


          {/* User Menu or CTA Button */}
          {!loading && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 pl-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getInitials(profile?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden xl:inline text-sm font-medium">
                    {profile?.full_name || user.email?.split('@')[0]}
                  </span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-background">
                <DropdownMenuItem asChild>
                  <Link to="/portal" className="flex items-center gap-2 cursor-pointer">
                    <LayoutDashboard className="h-4 w-4" />
                    {t('nav.portal')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2 cursor-pointer text-destructive">
                  <LogOut className="h-4 w-4" />
                  {t('auth.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap text-xs xl:text-sm px-3 xl:px-4">
              <Link to="/partnership">
                <span className="xl:hidden">{t('nav.becomePartnerShort')}</span>
                <span className="hidden xl:inline">{t('nav.becomePartner')}</span>
              </Link>
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={t("common.toggleMenu")}
        >
          {mobileMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-b border-border overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 space-y-4">
              {/* Language Selector — compact dropdown (mobile) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label={t("common.selectLanguage")}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full border border-border bg-muted/40 hover:bg-muted transition-colors text-xs font-semibold text-foreground/80"
                  >
                    <FlagIcon
                      country={(languages.find(l => l.code === i18n.language)?.country) || 'US'}
                      className="h-3 w-[18px] rounded-sm"
                    />
                    <span className="uppercase tracking-wide">{i18n.language?.slice(0, 2) || 'en'}</span>
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44 bg-background">
                  {languages.map((lang) => {
                    const active = i18n.language === lang.code;
                    return (
                      <DropdownMenuItem
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`flex items-center gap-2.5 cursor-pointer ${active ? 'bg-muted font-semibold' : ''}`}
                      >
                        <FlagIcon country={lang.country} className="h-3 w-[18px] rounded-sm" />
                        <span className="flex-1 text-sm">{lang.label}</span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{lang.code}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>


              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : 'text-foreground/80'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {/* User Actions Mobile */}
              {!loading && user ? (
                <div className="pt-4 border-t border-border space-y-2">
                  <Button asChild variant="outline" className="w-full justify-start gap-2">
                    <Link to="/portal" onClick={() => setMobileMenuOpen(false)}>
                      <LayoutDashboard className="h-4 w-4" />
                      {t('nav.portal')}
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 text-destructive hover:text-destructive"
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                    {t('auth.signOut')}
                  </Button>
                </div>
              ) : (
                <div className="mt-4">
                  <Button
                    asChild
                    size="sm"
                    className="inline-flex w-auto h-9 px-4 text-sm font-semibold rounded-md"
                  >
                    <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                      {t('nav.getStarted')}
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
